import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY = { title: "", content: "", client_id: "none", meeting_date: "" };

export default function TeamNoteFormDialog({ open, onOpenChange, category, note, clients, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!note;

  useEffect(() => {
    if (open) {
      setForm(
        note
          ? { title: note.title, content: note.content, client_id: note.client_id || "none", meeting_date: note.meeting_date || "" }
          : EMPTY
      );
    }
  }, [open, note]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and notes content are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        content: form.content.trim(),
        ...(category === "meeting" && { client_id: form.client_id === "none" ? null : form.client_id, meeting_date: form.meeting_date || null }),
      };
      if (isEdit) {
        await api.put(`/team-notes/${note.id}`, payload);
        toast.success("Note updated");
      } else {
        await api.post("/team-notes", { category, ...payload });
        toast.success("Note added");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="team-note-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? "Edit" : "Add"} {category === "gtm" ? "GTM Note" : "Meeting Note"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input data-testid="note-title-input" value={form.title} onChange={set("title")} placeholder="e.g. Weekly Sync - Jul 24" />
          </div>
          {category === "meeting" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Client (optional)</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="note-client-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Internal / No Client</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Meeting Date</Label>
                <Input data-testid="note-meeting-date-input" type="date" value={form.meeting_date} onChange={set("meeting_date")} />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>What was discussed</Label>
            <Textarea data-testid="note-content-input" value={form.content} onChange={set("content")} rows={6} placeholder="Key points, decisions, action items..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="note-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="note-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Add Note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
